import { ArrowUpRight, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT } from "../constants/contact";

const ContactRow = ({ label, value, href, testId, external = true }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    data-testid={testId}
    className="group flex items-center justify-between border-b border-white/10 py-6 transition-colors duration-300 hover:border-gold/50"
  >
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-bone/50">{label}</span>
    <span className="flex items-center gap-3 text-lg text-bone transition-colors duration-300 group-hover:text-gold sm:text-xl">
      {value}
      <ArrowUpRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
    </span>
  </a>
);

export const Contact = ({ t }) => (
  <section id="contactos" className="relative border-t border-white/5" data-testid="contact-section">
    <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold" data-testid="contact-chapter">
          {t.contact.chapter}
        </p>
        <h2 className="mt-4 font-display text-4xl tracking-tight text-bone sm:text-5xl" data-testid="contact-title">
          {t.contact.title}
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-giant-cta"
          className="group mt-16 block"
        >
          <p className="font-display text-[clamp(3.5rem,13vw,12rem)] leading-[0.9] tracking-tight text-bone transition-colors duration-500 group-hover:text-gold">
            {t.contact.ctaLine1}
            <br />
            <span className="italic">{t.contact.ctaLine2}</span>
          </p>
        </a>
      </Reveal>

      <Reveal delay={0.15}>
        <p className="mt-10 max-w-xl text-base leading-relaxed text-bone/60 sm:text-lg" data-testid="contact-pricing-note">
          {t.contact.pricing}
        </p>
      </Reveal>

      <div className="mt-20 grid gap-16 lg:grid-cols-2">
        <Reveal delay={0.1}>
          <div data-testid="contact-direct-list">
            <ContactRow label={t.contact.labels.whatsapp} value={CONTACT.phoneDisplay} href={CONTACT.whatsapp} testId="whatsapp-link" />
            <ContactRow label={t.contact.labels.phone} value={CONTACT.phoneDisplay} href={CONTACT.phoneHref} testId="phone-link" external={false} />
            <ContactRow label={t.contact.labels.email} value={CONTACT.email} href={`mailto:${CONTACT.email}`} testId="email-link" external={false} />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div data-testid="contact-social-list">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/40">{t.contact.follow}</p>
            <ContactRow label={t.contact.labels.instagram} value="@cebola_fishinguide" href={CONTACT.instagram} testId="instagram-link" />
            <ContactRow label={t.contact.labels.facebook} value="CebolaFishing Guide" href={CONTACT.facebook} testId="facebook-link" />
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-20 border-y border-white/10 py-8" data-testid="locations-strip">
          <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/40" data-testid="locations-label">
            {t.contact.locationsLabel}
          </p>
          <ul className="flex flex-wrap gap-x-10 gap-y-4">
            {t.contact.locations.map((loc) => (
              <li key={loc} className="flex items-center gap-2" data-testid={`location-${loc.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}>
                <MapPin className="h-4 w-4 text-gold" />
                <span className="font-display text-xl italic tracking-tight text-bone/80 sm:text-2xl">{loc}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>

    <footer className="border-t border-white/5" data-testid="site-footer">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-8 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40 sm:flex-row sm:items-center sm:justify-between lg:px-10">
        <span data-testid="footer-copy">{t.contact.footer}</span>
        <span data-testid="footer-location">{t.contact.location}</span>
      </div>
    </footer>
  </section>
);
